import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { BellIcon, SparklesIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { announcementsAPI, feedbackAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { getFixedDropdownPosition } from './dropdownPosition';

// Add custom animations
const customAnimations = `
  @keyframes wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-10deg); }
    75% { transform: rotate(10deg); }
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
  const [unreadRepliesCount, setUnreadRepliesCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user } = useAuth();

  // Determine the current dashboard path based on the current route
  const getCurrentDashboardPath = () => {
    const path = location.pathname;
    if (path === '/user-dashboard' || path.startsWith('/user-dashboard')) {
      return '/user-dashboard';
    } else if (path === '/manager-dashboard' || path.startsWith('/manager-dashboard')) {
      return '/manager-dashboard';
    } else if (path === '/' || path.startsWith('/attendance') || path.startsWith('/users') || path.startsWith('/settings')) {
      return '/';
    }
    // Default fallback
    return '/user-dashboard';
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await announcementsAPI.getUnreadCount();
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch feedback unread count (for managers/admins only)
  const fetchFeedbackUnreadCount = async () => {
    if (!isAdmin()) return;
    try {
      const response = await feedbackAPI.getUnreadCount();
      setFeedbackUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching feedback unread count:', error);
    }
  };

  // Fetch unread replies count (for employees)
  const fetchUnreadRepliesCount = async () => {
    if (isAdmin() || !user) return;
    try {
      const response = await feedbackAPI.getUnreadRepliesCount();
      setUnreadRepliesCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread replies count:', error);
    }
  };

  // Fetch recent announcements
  const fetchRecentAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await announcementsAPI.getAnnouncements({ unreadOnly: 'false' });
      const announcements = response.data.announcements || [];
      setRecentAnnouncements(announcements.slice(0, 5));
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
    fetchFeedbackUnreadCount();
    fetchUnreadRepliesCount();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchFeedbackUnreadCount();
      fetchUnreadRepliesCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isAdmin, user]);

  // Fetch recent announcements when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchRecentAnnouncements();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          dropdownContentRef.current && !dropdownContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update dropdown position when it opens or window changes
  useEffect(() => {
    if (!isOpen || !buttonRef.current || !dropdownContentRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdown = dropdownContentRef.current;
      if (!dropdown) return;

      const dropdownRect = dropdown.getBoundingClientRect();
      const { left, top, width, maxHeight } = getFixedDropdownPosition({
        triggerRect: rect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        preferredWidth: dropdownRect.width || 384,
        dropdownHeight: dropdownRect.height,
      });

      dropdown.style.top = `${top}px`;
      dropdown.style.left = `${left}px`;
      dropdown.style.right = 'auto';
      dropdown.style.width = `${width}px`;
      dropdown.style.maxHeight = `${Math.max(maxHeight, 240)}px`;
    };

    updatePosition();
    
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const handleAnnouncementClick = async (announcement) => {
    try {
      // Mark as read
      if (!announcement.isRead) {
        await announcementsAPI.markAsRead(announcement.id);
        fetchUnreadCount();
      }
      
      // Navigate to announcements page with this announcement selected and current dashboard info
      setIsOpen(false);
      const currentDashboard = getCurrentDashboardPath();
      navigate(`/announcements?id=${announcement.id}`, {
        state: { fromDashboard: currentDashboard }
      });
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    const currentDashboard = getCurrentDashboardPath();
    navigate('/announcements', {
      state: { fromDashboard: currentDashboard }
    });
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const truncateText = (text, maxLength = 60) => {
    // Strip HTML tags for preview
    const stripped = text.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Add custom animations */}
      <style>{customAnimations}</style>
      
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative inline-flex items-center justify-center p-2.5 rounded-xl text-white transition-all duration-300 ${
          unreadCount > 0 
            ? 'bg-white/20 hover:bg-white/30 shadow-lg hover:shadow-xl' 
            : 'bg-white/10 hover:bg-white/20'
        } backdrop-blur-md border-2 border-white/30 hover:border-white/50 focus:outline-none focus:ring-4 focus:ring-white/30 hover:scale-105`}
        title={unreadCount > 0 ? `${unreadCount} unread announcements` : 'Announcements'}
      >
        <BellIcon className={`h-5 w-5 sm:h-6 sm:w-6 transition-transform duration-300 ${
          unreadCount > 0 ? 'animate-[wiggle_1s_ease-in-out_infinite]' : ''
        }`} />
        
        {(unreadCount > 0 || feedbackUnreadCount > 0 || unreadRepliesCount > 0) && (
          <>
            {/* Pulsing Ring */}
            <span className="absolute -top-1 -right-1 flex h-6 w-6">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex items-center justify-center h-6 w-6 rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] font-bold shadow-lg border-2 border-white">
                {unreadCount + feedbackUnreadCount + unreadRepliesCount > 99 ? '99+' : unreadCount + feedbackUnreadCount + unreadRepliesCount}
              </span>
            </span>
          </>
        )}
      </button>

      {/* Dropdown - Rendered via Portal to body */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownContentRef}
          className="fixed flex max-h-[calc(100vh-16px)] flex-col overflow-hidden rounded-2xl border-2 border-gray-100 bg-white shadow-2xl animate-[slideDown_0.2s_ease-out]"
          style={{
            zIndex: 99999,
            maxWidth: 'calc(100vw - 16px)'
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BellIcon className="h-6 w-6" />
                <h3 className="text-lg font-bold">Announcements</h3>
              </div>
              {unreadCount > 0 && (
                <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full">
                  <SparklesIcon className="h-4 w-4" />
                  <span className="text-sm font-semibold">{unreadCount} New</span>
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="mt-2 text-sm text-gray-500">Loading...</p>
              </div>
            ) : recentAnnouncements.length === 0 ? (
              <div className="p-8 text-center">
                <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No announcements yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentAnnouncements.map((announcement, index) => (
                  <button
                    key={announcement.id}
                    onClick={() => handleAnnouncementClick(announcement)}
                    className={`w-full text-left p-4 transition-all duration-200 ${
                      !announcement.isRead 
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-l-4 border-indigo-500' 
                        : 'hover:bg-gray-50'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className={`text-sm font-semibold ${
                            !announcement.isRead ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {announcement.title}
                          </p>
                          {!announcement.isRead && (
                            <span className="flex h-2 w-2">
                              <span className="animate-ping absolute h-2 w-2 rounded-full bg-indigo-400 opacity-75"></span>
                              <span className="relative h-2 w-2 rounded-full bg-indigo-500"></span>
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {truncateText(announcement.content)}
                        </p>
                        <div className="flex items-center mt-2 space-x-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            announcement.priority === 'high' 
                              ? 'bg-red-100 text-red-700' 
                              : announcement.priority === 'normal'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {announcement.priority.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500 font-medium">
                            {formatDate(announcement.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-gray-50 border-t-2 border-gray-100 space-y-2">
            <button
              onClick={handleViewAll}
              className="w-full text-center text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 py-2.5 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
            >
              View All Announcements →
            </button>
            {isAdmin() && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/feedback-inbox');
                }}
                className={`w-full text-center text-sm font-bold text-white py-2.5 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center ${
                  feedbackUnreadCount > 0
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                    : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
                }`}
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                Feedback Inbox{feedbackUnreadCount > 0 ? ` (${feedbackUnreadCount})` : ''}
              </button>
            )}
            {!isAdmin() && unreadRepliesCount > 0 && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/my-feedback');
                }}
                className="w-full text-center text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-2.5 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                My Feedback - {unreadRepliesCount} New Repl{unreadRepliesCount > 1 ? 'ies' : 'y'}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default NotificationBell;
